import React, { useState, useRef } from "react";
import Styles from "./Element.module.css";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";
import { useSelector } from "react-redux";
import SelectInput from "../../../../commons/Inputs/Select/SelectInput";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { getIfscDetails } from "../../../../Services/ApiCalling/PublicApi";
import { 
  sanitizeContactName, 
  sanitizeIfsc, 
  CONTACT_NAME_REGEX, 
  IFSC_REGEX, 
  GENERAL_ERRORS,
  ACCOUNT_NUMBER_REGEX,
  sanitizeGeneralText
} from "../../../../helpers/validators/generalValidators";

const BankDetails = ({ userData, handleData, handleProceed }) => {
  const publicData = useSelector((state) => state?.publicSlice);

  // State Management
  const [formData, setFormData] = useState({
    bankName: userData.bankName || "",
    payee: userData.payee || "",
    bankAddress: userData.bankAddress || "",
    accountNo: userData.accountNo || "",
    ifsc: userData.ifsc || "",
    rating: userData.rating || {},
    ratingYear: userData.ratingYear || "",
    agency: userData.agency || "",
    byRules: false,
    byLaw: false,
  });

  const [errors, setErrors] = useState({});
  const [ifscLoading, setIfscLoading] = useState(false);
  const [ifscError, setIfscError] = useState("");
  const [autoFilledFields, setAutoFilledFields] = useState(new Set());
  const latestSearch = useRef("");

  // Handlers
   const handleChange = (field, value) => {
    let formattedValue = value;
    if (field === "payee") {
      formattedValue = sanitizeContactName(value).slice(0, 50);
    }
    if (field === "bankName") {
      formattedValue = sanitizeGeneralText(value).slice(0, 100);
    }
    if (field === "bankAddress") {
      formattedValue = sanitizeGeneralText(value).slice(0, 250);
    }
    if (["ratingYear", "agency"].includes(field)) {
      formattedValue = sanitizeGeneralText(value);
    }
    setFormData((prev) => ({ ...prev, [field]: formattedValue }));

    if (errors[field]) {
      setErrors((prevErrors) => {
        const { [field]: removedError, ...rest } = prevErrors;
        return rest;
      });
    }
  };

  const handleIfscChange = async (value) => {
    value = sanitizeIfsc(value);
    handleChange("ifsc", value);
    setIfscError("");

    if (!IFSC_REGEX.test(value)) return;

    latestSearch.current = value;
    const currentSearch = value;

    setIfscLoading(true);
    const res = await getIfscDetails(value);

    if (latestSearch.current !== currentSearch) return;

    // if (res && res.data && res.data.code === 200) {
    //   const data = res.data.data;
    //   if (!data || !data.bank || !data.branch) {
    //     setIfscError("Invalid IFSC — check and try again");
    //     setIfscLoading(false);
    //     return;
    //   }

    //   const { bank, branch } = data;

    //   setFormData((prev) => ({
    //     ...prev,
    //     bankName: (!prev.bankName || autoFilledFields.has("bankName")) ? bank : prev.bankName,
    //     bankAddress: (!prev.bankAddress || autoFilledFields.has("bankAddress")) ? branch : prev.bankAddress,
    //   }));

    //   setErrors((prevErrors) => {
    //     const nextErrors = { ...prevErrors };
    //     delete nextErrors.bankName;
    //     delete nextErrors.bankAddress;
    //     return nextErrors;
    //   });

    //   setAutoFilledFields(new Set(["bankName", "bankAddress"]));
    // } else {
    //   const errorMsg = res?.data?.message || res?.message || "Could not fetch bank details.";
    //   setIfscError(errorMsg);
    // }
    
    setIfscLoading(false);
  };

  const unlockField = (fieldName) => {
    setAutoFilledFields((prev) => {
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
  };

  const validateFields = () => {
    const requiredFields = [
      "bankName",
      "bankAddress",
      "payee",
      "accountNo",
      "ifsc",
      "rating",
      "ratingYear",
      "agency",
    ];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase())} is required`;
      }
    });
    
    if (formData.ifsc && !IFSC_REGEX.test(formData.ifsc)) {
      newErrors.ifsc = GENERAL_ERRORS.ifsc;
    }


    if (formData.payee && !CONTACT_NAME_REGEX.test(formData.payee)) {
      newErrors.payee = GENERAL_ERRORS.payee;
    } else if (formData.payee && formData.payee.length > 50) {
      newErrors.payee = "Payee name cannot exceed 50 characters";
    }

    if (formData.accountNo && !ACCOUNT_NUMBER_REGEX.test(formData.accountNo)) {
      newErrors.accountNo = GENERAL_ERRORS.accountNumber;
    }

    if (formData.bankName && formData.bankName.length > 100) {
      newErrors.bankName = "Bank name cannot exceed 100 characters";
    }

    if (formData.bankAddress && formData.bankAddress.length > 250) {
      newErrors.bankAddress = "Bank address cannot exceed 250 characters";
    }

    if (!formData.rating?.id) {
      newErrors.rating = "Rating is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = (data) => {
    if (!validateFields()) return;
    if (ifscLoading) return;

    const bankRating = {
      bankAccounts: [
        {
          nameAddress: `${formData.bankName} ${formData.bankAddress}`,
          payee: formData.payee,
          accNo: formData.accountNo,
          ifsc: formData.ifsc,
          operationalAt: "",
        },
      ],
      creditRatingId: formData.rating,
      creditRatingYear: formData.ratingYear,
      creditRatingAgency: formData.agency,
      isAcceptedByLaws: formData.byLaw,
      isAcceptedBusinessRule: formData.byRules,
    };

    handleData("bankRating", bankRating);
    handleProceed(data);
  };

  // Render Helpers
  const renderCheckbox = (label, field) => (
    <div
      className={Styles.checkSelction}
      onClick={() => handleChange(field, !formData[field])}
    >
      {formData[field] ? (
        <CheckBoxIcon style={{ fontSize: "24px", color: "var(--primary-color-text)" }} />
      ) : (
        <CheckBoxOutlineBlankIcon
          style={{ fontSize: "24px", color: "#6f6d7c" }}
        />
      )}
      <div>
        <span>I Understand and Accept the</span>
        <span style={{ color: "var(--primary-color-text)", paddingLeft: "8px" }}>{label}</span>
      </div>
    </div>
  );

  const renderTextInput = (
    field,
    heading,
    example,
    type = "text",
    readOnly = false,
  ) => {
    let maxLength = undefined;
    if (field === "payee") maxLength = 50;
    if (field === "bankName") maxLength = 100;
    if (field === "bankAddress") maxLength = 250;

    return (
      <TextInput
        heading={`${heading} *`}
        value={formData[field]}
        onChange={(value) => handleChange(field, value)}
        maxLength={maxLength}
        example={example}
        type={type}
        height="45"
        error={errors[field]}
        readOnly={readOnly}
      />
    );
  };

  const renderAutofillInput = (
    field,
    heading,
    example,
    type = "text",
    readOnly = false
  ) => {
    const isAutoFilled = autoFilledFields.has(field);
    let maxLength = undefined;
    if (field === "payee") maxLength = 50;
    if (field === "bankName") maxLength = 100;
    if (field === "bankAddress") maxLength = 250;

    return (
      <div style={{ position: "relative" }}> 
        <TextInput
          heading={`${heading} `}
          value={formData[field]}
          onChange={(value) => handleChange(field, value)}
          maxLength={maxLength}
          example={example}
          type={type}
          height="45"
          error={errors[field]}
          readOnly={readOnly}
        />
        {isAutoFilled && (
          <span
            onClick={() => unlockField(field)}
            style={{
              fontSize: "11px",
              color: "var(--primary-color-text)",
              cursor: "pointer",
              position: "absolute",
              top: "4px",
              right: "8px"
            }}
          >
            🖊 Edit
          </span>
        )}
      </div>
    );
  };

  const renderIfscInput = () => (
    <div>
      <TextInput
        heading="IFSC Code *"
        value={formData.ifsc}
        onChange={(value) => handleIfscChange(value)}
        example={ifscLoading ? "Fetching bank details..." : "Eg. HDFC0001234"}
        height="45"
        error={errors.ifsc || ifscError}
      />
      {ifscLoading && (
        <span style={{ fontSize: "12px", color: "var(--primary-color-text)", marginTop: "4px", display: "block" }}>
          🔍 Looking up bank details...
        </span>
      )}
    </div>
  );


  const renderSelectInput = (field, heading, list, example) => (
    <SelectInput
      onChange={(value) => handleChange(field, value)}
      list={list}
      value={formData[field]}
      example={example}
      heading={`${heading} *`}
      height="45px"
      error={errors[field]}
    />
  );

  // Main Render
  return (
    <div className={Styles.elementMain}>
      <div className={Styles.displayInputSection}>
        <div className={Styles.conterntHeader}>Bank Details</div>
        <div className={Styles.layer3ElementsContainer}>
          {renderTextInput(
            "accountNo",
            "Account Number *",
            "Enter Account Number",
          )}
          {renderTextInput("payee", "Payee Name *", "Eg. John Doe")}
          {renderIfscInput()}
        </div>
        <div className={Styles.layer2ElementsContainer}>
          {renderAutofillInput("bankName", "Bank Name *", "Enter Bank Name")}
          {renderAutofillInput(
            "bankAddress",
            "Branch Address *",
            "Enter Branch Address",
          )}
        </div>

        <div className={Styles.conterntHeader}>Rating</div>
        <div className={Styles.layer3ElementsContainer}>
          {renderSelectInput(
            "rating",
            "Credit Rating",
            publicData?.publicRating,
            "Eg. Select Credit Rating",
          )}
          {renderTextInput("ratingYear", "Credit Rating Year", "Eg. FEB 2025")}
          {renderTextInput("agency", "Credit Rating Agency", "Eg. GNA Energy")}
        </div>
        {renderCheckbox("Business Rules", "byRules")}
        {renderCheckbox("Bye Laws", "byLaw")}
      </div>
      <div className={Styles.buttonSection}>
        <div className={Styles.prevBtn} onClick={() => handleProceed("prev")}>
          Back
        </div>
        <div className={Styles.nextBtn} onClick={() => handleContinue("next")}>
          Next
        </div>
      </div>
    </div>
  );
};

export default BankDetails;
